import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { GitCompare, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => (typeof n === "number" ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—");
const RED = "#b23a48", GREEN = "#1e7d5a";
const short = (l) => (l.location || l.company_name || "").split(",")[0];

export default function LocationCompare({ company, industry, locations, cache, onCache, onClose, onCompare }) {
  const initial = locations.length >= 2 ? [0, 1] : [0];
  const [sel, setSel] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null); // [{ loc, data }]

  const key = (l) => `${(l.url || "").toLowerCase()}|${(l.location || "").toLowerCase().trim()}`;
  const toggle = (i) => setSel((s) => (s.includes(i) ? (s.length > 1 ? s.filter((x) => x !== i) : s) : [...s, i].sort((a, b) => a - b)));
  const allOn = sel.length === locations.length;
  const selectAll = () => setSel(allOn ? [0] : locations.map((_, i) => i));

  const fetchOne = async (l) => {
    if (cache[key(l)]) return cache[key(l)];
    const { data } = await api.post("/bbb/business-financials", {
      company_name: company, location: l.location || null, bbb_rating: l.bbb_rating || null,
      complaints: typeof l.complaints === "number" ? l.complaints : null, industry: industry || null,
      website: l.website || null, bbb_url: l.url && l.url.includes("bbb.org") ? l.url : null, scope: "location",
    });
    onCache(key(l), data);
    return data;
  };

  const top = (f) => (f.loss_drivers || []).slice().sort((x, y) => (y.amount || 0) - (x.amount || 0))[0];

  const run = async () => {
    if (sel.length < 2) { toast.error("Pick at least two locations to compare."); return; }
    setBusy(true); setRes(null);
    try {
      const picks = sel.map((i) => locations[i]);
      const datas = await Promise.all(picks.map((l) => fetchOne(l)));
      const rows = picks.map((loc, i) => ({ loc, data: datas[i] }));
      setRes(rows);
      // Lift the comparison up so the report PDF can include exactly what's on screen.
      onCompare?.({
        locations: rows.map(({ loc, data }) => {
          const fin = data?.financials || {};
          return {
            location: loc.location || null, company_name: company,
            bbb_rating: loc.bbb_rating || null,
            complaints: typeof loc.complaints === "number" ? loc.complaints : null,
            revenue: Number(fin.estimated_annual_revenue) || null,
            net_profit: Number(fin.estimated_net_profit) || null,
            leaking: Number(fin.reported_profit_loss) || 0,
            recoverable: Number(fin.recoverable_with_elevare) || null,
            fraud_score: data?.fraud_risk?.score ?? null,
            fraud_exposure_high: data?.fraud_risk?.exposure_high ?? null,
            biggest_leak: top(fin)?.label || null,
            top_action: (() => {
              const ca = data?.chart_actions || {};
              const p = ca.loss_drivers || ca.profit_swing || ca.survival || ca.expansion || {};
              const a = (p.actions || [])[0];
              return a ? { title: a.title, how: a.how } : null;
            })(),
            web_visits: (data?.web_analytics?.has_website && Number(data.web_analytics.monthly_visits)) || null,
            top_source: (() => {
              const s = (data?.web_analytics?.sources || []).slice().sort((x, y) => (y.pct || 0) - (x.pct || 0))[0];
              return s ? `${s.label} (${s.pct}%)` : null;
            })(),
          };
        }),
      });
    } catch { toast.error("Could not compare these locations."); }
    finally { setBusy(false); }
  };

  const fins = res ? res.map((r) => r.data?.financials || {}) : [];
  const leaks = fins.map((f) => Number(f.reported_profit_loss) || 0);
  const worstIdx = res && leaks.length ? leaks.indexOf(Math.max(...leaks)) : -1;
  const chart = res ? res.map((r, i) => ({ name: short(r.loc), "Leaking today": leaks[i], "Recovered with Elevare": Number(fins[i].recoverable_with_elevare) || 0 })) : [];

  // Metric rows: highlight the weakest cell per row across all locations.
  const rowsDef = [
    { label: "BBB rating", get: (r) => r.loc.bbb_rating || "NR", fmt: (v) => v, cmp: null },
    { label: "Complaints on file", get: (r) => (typeof r.loc.complaints === "number" ? r.loc.complaints : null), fmt: (v) => (v == null ? "—" : String(v)), cmp: "max" },
    { label: "Est. revenue / yr", get: (r) => Number(r.data?.financials?.estimated_annual_revenue) || null, fmt: money, cmp: "min" },
    { label: "Est. net profit / yr", get: (r) => Number(r.data?.financials?.estimated_net_profit) || null, fmt: money, cmp: "min" },
    { label: "Leaking today / yr", get: (r) => Number(r.data?.financials?.reported_profit_loss) || 0, fmt: money, cmp: "max" },
    { label: "Recoverable with Elevare / yr", get: (r) => Number(r.data?.financials?.recoverable_with_elevare) || null, fmt: money, cmp: "min" },
    { label: "Fraud risk score", get: (r) => r.data?.fraud_risk?.score ?? null, fmt: (v) => (v == null ? "—" : `${v}/100`), cmp: "max" },
    { label: "Est. fraud exposure / yr", get: (r) => r.data?.fraud_risk?.exposure_high ?? null, fmt: money, cmp: "max" },
    { label: "Website visits / mo", get: (r) => (r.data?.web_analytics?.has_website ? Number(r.data.web_analytics.monthly_visits) || null : null), fmt: (v) => (v == null ? "—" : v.toLocaleString()), cmp: "min" },
    { label: "Top traffic source", get: (r) => { const s = (r.data?.web_analytics?.sources || []).slice().sort((x, y) => (y.pct || 0) - (x.pct || 0))[0]; return s ? `${s.label} (${s.pct}%)` : "—"; }, fmt: (v) => v, cmp: null },
    { label: "Biggest leak", get: (r) => top(r.data?.financials || {})?.label || "—", fmt: (v) => v, cmp: null },
  ];
  const worstCell = (rowDef) => {
    if (!res || !rowDef.cmp) return -1;
    const vals = res.map((r) => rowDef.get(r)).map((v) => (typeof v === "number" ? v : null));
    const nums = vals.filter((v) => v != null);
    if (nums.length < 2 || nums.every((v) => v === nums[0])) return -1;
    const target = rowDef.cmp === "max" ? Math.max(...nums) : Math.min(...nums);
    return vals.indexOf(target);
  };

  return (
    <div data-testid="location-compare" className="rounded-2xl border border-[#1e3d3a] bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 flex items-center gap-1.5"><GitCompare className="h-3.5 w-3.5 text-[#c9a961]" /> Compare locations</p>
          <p className="mt-1 text-sm text-[#1e3d3a]/65">Tick any locations (or All) to see them side-by-side. Whatever you compare here is added to the downloadable report PDF. Red is where the company stands today; green is with Elevare.</p>
        </div>
        <button type="button" data-testid="compare-close" onClick={onClose} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300"><X className="h-4 w-4" /></button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="button" data-testid="compare-select-all" onClick={selectAll}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${allOn ? "bg-[#1e3d3a] text-white" : "border border-[#1e3d3a]/20 text-[#1e3d3a]/70 hover:border-[#c9a961]"}`}>
          <Check className="h-3 w-3" /> {allOn ? "All selected" : `All ${locations.length}`}
        </button>
        {locations.map((l, i) => {
          const on = sel.includes(i);
          return (
            <button key={key(l) || i} type="button" data-testid={`compare-pick-${i}`} onClick={() => toggle(i)} disabled={busy}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] transition-colors duration-300 ${on ? "bg-[#c9a961]/20 border border-[#c9a961] text-[#a3823f]" : "border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:border-[#c9a961]/50"}`}>
              {on && <Check className="h-3 w-3" />} {short(l)}{l.bbb_rating ? ` · ${l.bbb_rating}` : ""}
            </button>
          );
        })}
        <button type="button" data-testid="compare-run" onClick={run} disabled={busy || sel.length < 2}
          className="ml-auto inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitCompare className="h-3.5 w-3.5" />} Compare {sel.length}
        </button>
      </div>

      {busy && <p className="mt-4 text-xs text-[#1e3d3a]/50">Estimating {sel.length} locations… this takes about {Math.max(30, sel.length * 20)} seconds.</p>}

      {res && (
        <div className="mt-5 space-y-4">
          {worstIdx >= 0 && (
            <div data-testid="compare-verdict" className="rounded-xl border border-[#b23a48]/30 bg-[#b23a48]/5 px-4 py-3 text-sm text-[#1e3d3a]">
              <span className="font-semibold text-[#b23a48]">{res[worstIdx].loc.location || short(res[worstIdx].loc)}</span> is leaking the most — <span className="font-semibold">{money(leaks[worstIdx])}/yr</span>. Lead the pitch there.
            </div>
          )}

          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                <YAxis domain={[0, "auto"]} allowDataOverflow tick={{ fontSize: 9 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={42} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="Leaking today" fill={RED} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="Recovered with Elevare" fill={GREEN} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <table data-testid="compare-table" className="w-full text-xs">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">
                  <th className="py-2 pr-3 font-normal sticky left-0 bg-white">Metric</th>
                  {res.map((r, i) => <th key={i} className="py-2 px-3 font-normal text-right whitespace-nowrap">{short(r.loc)}</th>)}
                </tr>
              </thead>
              <tbody>
                {rowsDef.map((rd, ri) => {
                  const worst = worstCell(rd);
                  return (
                    <tr key={ri} className="border-t border-[#1e3d3a]/8">
                      <td className="py-2 pr-3 text-[#1e3d3a]/60 sticky left-0 bg-white">{rd.label}</td>
                      {res.map((r, ci) => (
                        <td key={ci} className={`py-2 px-3 text-right tabular-nums whitespace-nowrap ${ci === worst ? "text-[#b23a48] font-semibold" : "text-[#1e3d3a]"}`}>{rd.fmt(rd.get(r))}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] italic text-[#1e3d3a]/45">Red highlights the weakest figure in each row. Financials are AI estimates from each location's BBB profile — verify before quoting. This comparison is included in the downloadable report PDF.</p>
        </div>
      )}
    </div>
  );
}
